import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Button, Container, InputGroup, FormControl, Form } from 'react-bootstrap';
import { CheckCircleFill } from 'react-bootstrap-icons';
import { Html5QrcodeScanner } from 'html5-qrcode';
import { toast } from 'react-toastify';
import JsBarcode from 'jsbarcode';
import api from '../api';
import debounce from 'lodash.debounce';

const BIN_ID_RANGE = { min: 1, max: 260 };

const BinAssignment = () => {
  const [scanning, setScanning] = useState(false);
  const [scanPhase, setScanPhase] = useState('sku'); // 'sku' or 'bin'
  const [autoCloseAfterAssignment, setAutoCloseAfterAssignment] = useState(false); // Toggle auto-close
  const [draftsWithoutBins, setDraftsWithoutBins] = useState([]);
  const [inventoryWithoutBins, setInventoryWithoutBins] = useState([]);
  const [lastScanned, setLastScanned] = useState({ type: null, data: null, isDraft: false, productId: null });
  const [skuScanStatus, setSkuScanStatus] = useState(null);
  const [scanner, setScanner] = useState(null);
  const [bins, setBins] = useState([]);
  const [selectedbin_id, setSelectedbin_id] = useState('');
  const [singleBinReady, setSingleBinReady] = useState(false);
  const [isGeneratingSingleBarcode, setIsGeneratingSingleBarcode] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('id');
  const [sortOrder, setSortOrder] = useState('asc');
  const [isLoadingBins, setIsLoadingBins] = useState(false);
  const [isLoadingDrafts, setIsLoadingDrafts] = useState(false);
  const [isLoadingInventory, setIsLoadingInventory] = useState(false);
  const [manualBarcode, setManualBarcode] = useState('');
  const [isScanning, setIsScanning] = useState(false);
  const [cameraPermission, setCameraPermission] = useState(null);
  const componentRef = useRef(null);
  const singleBinPrintRef = useRef(null);
  const scannerRef = useRef(null); // Store scanner instance

  const fetchBins = async () => {
  try {
    const res = await api.get('/bins');
    const validBins = res.data.filter(
      (bin) => bin.barcode !== 'NEEDS_BIN'
    );

    const idSet = new Set(validBins.map(bin => bin.id));
    if (idSet.size !== validBins.length) {
      console.error('Duplicate bin IDs detected:', validBins);
      toast.error('Duplicate bin IDs found in data');
    }
    console.log('Fetched bins:', validBins);
    setBins(validBins);
  } catch (error) {
    toast.error('Failed to fetch bins');
    console.error('Bins fetch error:', error);
    setBins([]);
  } finally {
    setIsLoadingBins(false);
  }
};

useEffect(() => {
  fetchBins(); // fetch bins on page load
}, []);


  // Fetch drafts without bins
  useEffect(() => {
    const fetchDraftsWithoutBins = async () => {
      setIsLoadingDrafts(true);
      try {
        const response = await api.get('/drafts/without-bins');
        setDraftsWithoutBins(response.data || []);
        toast.info(`${response.data.length} drafts need bin assignment`);
      } catch (error) {
        console.error('[fetchDraftsWithoutBins] Error:', error);
        if (error.response?.status === 404 || error.response?.status === 500) {
          setDraftsWithoutBins([]);
          toast.info(`No drafts found (server error: ${error.response?.status})`);
        } else {
          setDraftsWithoutBins([]);
          toast.error('Failed to fetch drafts without bins');
        }
      } finally {
        setIsLoadingDrafts(false);
      }
    };
    fetchDraftsWithoutBins();
  }, []);

  // Fetch inventory products without bins
  useEffect(() => {
    const fetchInventoryWithoutBins = async () => {
      setIsLoadingInventory(true);
      try {
        const response = await api.get('/products/inventory/without-bins');
        setInventoryWithoutBins(response.data || []);
        toast.info(`${response.data.length} inventory products need bin assignment`);
      } catch (error) {
        console.error('[fetchInventoryWithoutBins] Error:', error);
        if (error.response?.status === 404 || error.response?.status === 500) {
          setInventoryWithoutBins([]);
          toast.info(`No inventory products found (server error: ${error.response?.status})`);
        } else {
          setInventoryWithoutBins([]);
          toast.error('Failed to fetch inventory products without bins');
        }
      } finally {
        setIsLoadingInventory(false);
      }
    };
    fetchInventoryWithoutBins();
  }, []);

  // Generate barcodes for all bins
  useEffect(() => {
    if (bins.length > 0 && componentRef.current) {
      bins.forEach(bin => {
        const canvas = document.getElementById(`barcode-${bin.id}`);
        if (canvas && bin.barcode && typeof bin.barcode === 'string' && bin.barcode.trim()) {
          try {
            JsBarcode(canvas, bin.barcode, {
              format: 'CODE128',
              width: 2,
              height: 50,
              displayValue: true,
              fontSize: 14,
            });
            console.log(`Barcode generated for bin ${bin.id}`);
          } catch (error) {
            console.error(`Failed to generate barcode for bin ${bin.id}:`, error);
            toast.error(`Failed to generate barcode for bin ${bin.id}`);
          }
        } else {
          console.warn(`Invalid canvas/barcode for bin ${bin.id}`, { canvas: !!canvas, barcode: bin.barcode });
        }
      });
    }
  }, [bins]);

  // Generate single bin barcode
  useEffect(() => {
    if (!selectedbin_id || !singleBinPrintRef.current) {
      setSingleBinReady(false);
      setIsGeneratingSingleBarcode(false);
      return;
    }
    const bin_id = parseInt(selectedbin_id);
    if (isNaN(bin_id) || bin_id < BIN_ID_RANGE.min || bin_id > BIN_ID_RANGE.max) {
      console.error(`Invalid bin ID: ${selectedbin_id}, must be ${BIN_ID_RANGE.min}–${BIN_ID_RANGE.max}`);
      toast.error(`Invalid bin ID (must be ${BIN_ID_RANGE.min}–${BIN_ID_RANGE.max})`);
      setSelectedbin_id('');
      setSingleBinReady(false);
      setIsGeneratingSingleBarcode(false);
      return;
    }
    const bin = bins.find(b => b.id === bin_id);
    if (bin && bin.barcode && typeof bin.barcode === 'string' && bin.barcode.trim()) {
      setIsGeneratingSingleBarcode(true);
      try {
        let canvas = singleBinPrintRef.current.querySelector('canvas');
        if (!canvas) {
          canvas = document.createElement('canvas');
          canvas.id = 'single-bin-barcode';
          singleBinPrintRef.current.appendChild(canvas);
        }
        JsBarcode(canvas, bin.barcode, {
          format: 'CODE128',
          width: 2,
          height: 50,
          displayValue: true,
          fontSize: 14,
        });
        const nameEl = singleBinPrintRef.current.querySelector('.bin-name');
        const locationEl = singleBinPrintRef.current.querySelector('.bin-location');
        if (nameEl) nameEl.textContent = bin.name || `Bin ${bin.id}`;
        if (locationEl) locationEl.textContent = `Location: ${bin.location || 'N/A'}`;
        console.log(`Single barcode generated for bin ${bin.id} (selected: ${selectedbin_id})`);
        setSingleBinReady(true);
      } catch (error) {
        console.error(`Failed to generate single barcode for bin ${bin.id}:`, error);
        toast.error(`Failed to generate barcode for bin ${bin.id}`);
        setSingleBinReady(false);
      } finally {
        setIsGeneratingSingleBarcode(false);
      }
    } else {
      console.warn(`Invalid bin or barcode for bin ${bin_id}`, { bin, selectedbin_id });
      toast.error(`Bin ${bin_id} not found or invalid barcode`);
      setSingleBinReady(false);
      setIsGeneratingSingleBarcode(false);
    }
  }, [selectedbin_id, bins]);

  // Check camera permissions
  const checkCameraPermissions = useCallback(async () => {
    try {
      console.log('[checkCameraPermissions] Requesting camera permissions');
      const devices = await navigator.mediaDevices.enumerateDevices();
      const hasCamera = devices.some(device => device.kind === 'videoinput');
      if (!hasCamera) {
        console.error('[checkCameraPermissions] No camera found');
        toast.error('No camera detected on this device');
        setCameraPermission('denied');
        return false;
      }
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
      stream.getTracks().forEach(track => track.stop());
      console.log('[checkCameraPermissions] Camera permission granted');
      setCameraPermission('granted');
      return true;
    } catch (error) {
      console.error('[checkCameraPermissions] Permission error:', error);
      toast.error('Camera access denied. Please enable camera permissions in your browser settings.');
      setCameraPermission('denied');
      return false;
    }
  }, []);

  // Initialize scanner
  const debouncedHandleScan = useCallback(
    debounce((err, result) => {
      if (err || !result || isScanning) {
        console.log('[debouncedHandleScan] Skipping scan:', { err, result, isScanning });
        return;
      }
      const scannedData = result.text;
      console.log('[debouncedHandleScan] Processing scan:', scannedData);
      setIsScanning(true);
      handleScanLogic(scannedData).finally(() => {
        setIsScanning(false);
      });
    }, 300),
    [isScanning]
  );

  useEffect(() => {
    console.log('[useEffect] Scanner effect triggered, scanning:', scanning, 'cameraPermission:', cameraPermission);
    if (scanning && cameraPermission === 'granted' && !scannerRef.current) {
      console.log('[useEffect] Starting scanner initialization');
      const html5QrcodeScanner = new Html5QrcodeScanner('scanner-container', { fps: 5, qrbox: 150 }, false);
      html5QrcodeScanner.render(
        (decodedText) => {
          console.log('[useEffect] Scan detected:', decodedText);
          debouncedHandleScan(null, { text: decodedText });
        },
        (error) => {
          console.warn('[useEffect] Scanner error:', error);
        }
      );
      scannerRef.current = html5QrcodeScanner;
      setScanner(html5QrcodeScanner);
    } else if (scanning && cameraPermission === null) {
      console.log('[useEffect] Checking camera permissions');
      checkCameraPermissions().then(granted => {
        if (!granted) {
          setScanning(false);
        }
      });
    }
    return () => {
      console.log('[useEffect] Cleaning up scanner');
      if (scannerRef.current && typeof scannerRef.current.clear === 'function') {
        scannerRef.current.clear().catch(err => console.error('[useEffect] Failed to clear scanner:', err));
        scannerRef.current = null;
      }
      setScanner(null);
    };
  }, [scanning, cameraPermission, checkCameraPermissions, debouncedHandleScan]);

  // Handle barcode scan logic
  const handleScanLogic = async (scannedData) => {
    console.log('[handleScanLogic] Starting for:', scannedData, 'phase:', scanPhase);
    try {
      if (scanPhase === 'sku') {
        console.log(`[handleScan] Scanning SKU/Barcode: ${scannedData}`);
        let draft = null;
        let product = null;

        // Check for draft by barcode or SKU
        try {
          const draftResponse = await api.get(`/drafts?barcode=${encodeURIComponent(scannedData)}`);
          if (draftResponse.data && typeof draftResponse.data === 'object') {
            draft = draftResponse.data;
          }
        } catch (error) {
          console.log('[handleScan] No draft found by barcode:', error.response?.status);
        }
        if (!draft) {
          try {
            const draftBySkuResponse = await api.get(`/drafts?sku=${encodeURIComponent(scannedData)}`);
            if (draftBySkuResponse.data && typeof draftBySkuResponse.data === 'object') {
              draft = draftBySkuResponse.data;
            }
          } catch (error) {
            console.log('[handleScan] No draft found by SKU:', error.response?.status);
          }
        }

        // Check for inventory product by barcode or SKU
        if (!draft) {
          try {
            const productResponse = await api.get(`/products?barcode=${encodeURIComponent(scannedData)}`);
            if (productResponse.data && typeof productResponse.data === 'object') {
              product = productResponse.data;
            }
          } catch (error) {
            console.log('[handleScan] No product found by barcode:', error.response?.status);
          }
        }
        if (!draft && !product) {
          try {
            const productBySkuResponse = await api.get(`/products?sku=${encodeURIComponent(scannedData)}`);
            if (productBySkuResponse.data && typeof productBySkuResponse.data === 'object') {
              product = productBySkuResponse.data;
            }
          } catch (error) {
            console.log('[handleScan] No product found by SKU:', error.response?.status);
          }
        }

        if (draft || product) {
          const isDraft = !!draft;
          console.log('[handleScan] Setting lastScanned:', { type: 'product', data: scannedData, isDraft, productId: product?.id });
          setLastScanned({ type: 'product', data: scannedData, isDraft, productId: product?.id });
          setSkuScanStatus('success');
          setScanPhase('bin');
          toast.info(`${isDraft ? 'Draft' : 'Inventory Product'} scanned. Scan bin barcode next.`);
        } else {
          console.log('[handleScan] No match found for:', scannedData);
          toast.error(`No product or draft found for barcode/SKU: ${scannedData}`);
          setSkuScanStatus('error');
        }
      } else if (scanPhase === 'bin') {
        console.log(`[handleScan] Scanning bin: ${scannedData}`);
        let binResponse = null;
        try {
          binResponse = await api.get(`/bins?barcode=${encodeURIComponent(scannedData)}`);
          console.log('[handleScan] Bin response:', binResponse.data);
        } catch (error) {
          console.log('[handleScan] No bin found by barcode:', error.response?.status);
          toast.error('Invalid bin barcode. Try again.');
          return;
        }
        if (!binResponse.data || binResponse.data.id < BIN_ID_RANGE.min || binResponse.data.id > BIN_ID_RANGE.max) {
          console.log('[handleScan] Invalid bin:', binResponse.data);
          toast.error('Invalid bin barcode. Try again.');
          return;
        }
        const bin_id = binResponse.data.id;
        const identifier = lastScanned.data;
        try {
          if (lastScanned.isDraft) {
            const draftResponse = await api.post('/drafts/assign-bin', { barcode: identifier, bin_id });
            console.log('[handleScan] Draft assignment response:', draftResponse.data);
            if (draftResponse.data.success) {
              toast.success(`Draft assigned to bin ${binResponse.data.name || `ID ${bin_id}`}`);
              if (draftResponse.data.reassigned) {
                toast.info('Previous bin assignment overwritten');
              }
              const updatedDrafts = await api.get('/drafts/without-bins');
              setDraftsWithoutBins(updatedDrafts.data);
            }
          } else {
            const inventoryResponse = await api.post('/products/inventory/assign-bin', { productId: lastScanned.productId, bin_id });
            console.log('[handleScan] Inventory assignment response:', inventoryResponse.data);
            toast.success(`Inventory product assigned to bin ${binResponse.data.name || `ID ${bin_id}`}`);
            const updatedInventory = await api.get('/products/inventory/without-bins');
            console.log('Updated inventory without bins:', updatedInventory.data);
            setInventoryWithoutBins(updatedInventory.data);
          }
          setLastScanned({ type: null, data: null, isDraft: false, productId: null });
          setSkuScanStatus(null);
          setScanPhase('sku');
          if (autoCloseAfterAssignment) {
            setScanning(false);
            toast.info('Scanning stopped. Start again to scan another SKU.');
          } else {
            toast.info('Ready to scan next SKU.');
          }
        } catch (assignmentError) {
          console.error('[handleScan] Assignment error:', assignmentError.response?.data || assignmentError.message);
          toast.error(`Failed to assign to bin: ${assignmentError.response?.data?.error || assignmentError.message}`);
        }
      }
    } catch (error) {
      console.error('[handleScanLogic] Unexpected error:', error);
      toast.error(`Error scanning barcode: ${error.message}`);
      setSkuScanStatus('error');
    } finally {
      console.log('[handleScanLogic] Completed for:', scannedData);
      setIsScanning(false);
    }
  };

  // Handle manual barcode input
  const handleManualSubmit = async () => {
    if (manualBarcode && !isScanning) {
      console.log('[handleManualSubmit] Processing manual input:', manualBarcode);
      setIsScanning(true);
      try {
        await handleScanLogic(manualBarcode);
      } catch (error) {
        console.error('[handleManualSubmit] Error:', error);
        toast.error(`Manual input error: ${error.message}`);
      } finally {
        setManualBarcode('');
        setIsScanning(false);
      }
    }
  };

  // Reset scan state
  const handleResetScan = () => {
    console.log('[handleResetScan] Resetting scan state');
    setLastScanned({ type: null, data: null, isDraft: false, productId: null });
    setSkuScanStatus(null);
    setScanPhase('sku');
    setManualBarcode('');
    toast.info('Scan state reset. Ready to scan SKU.');
  };

  // Print all bins (via server-generated PDF)
const printAllBins = async () => {
  if (bins.length === 0) {
    toast.warn('No bins available to print');
    return;
  }

  try {
    const token = localStorage.getItem('token'); // Adjust if you store token differently
    const response = await fetch('/api/bins/print-labels', {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });

    if (!response.ok) {
      throw new Error('Failed to fetch PDF');
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);

    // Open in new tab and auto-print
    const printWindow = window.open(url);
    if (printWindow) {
      printWindow.onload = () => {
        printWindow.focus();
        printWindow.print();
      };
    } else {
      toast.error('Popup blocked. Please allow popups for this site.');
    }

    console.log(`[${new Date().toISOString()}] [BinAssignment] Printed all bin barcodes`);
  } catch (error) {
    console.error('Error printing all bin barcodes:', error);
    toast.error('Failed to print bin barcodes');
  }
};


  // Print single bin
  const printSingleBin = () => {
    if (!selectedbin_id) {
      toast.warn('Please select a bin to print');
      return;
    }
    const bin_id = parseInt(selectedbin_id);
    if (isNaN(bin_id) || bin_id < BIN_ID_RANGE.min || bin_id > BIN_ID_RANGE.max) {
      toast.error(`Invalid bin ID (must be ${BIN_ID_RANGE.min}–${BIN_ID_RANGE.max})`);
      setSelectedbin_id('');
      setSingleBinReady(false);
      return;
    }
    if (!singleBinReady || isGeneratingSingleBarcode) {
      console.error('Single bin print not ready', { singleBinReady, isGeneratingSingleBarcode });
      toast.error('Single bin print content not ready');
      return;
    }
    const bin = bins.find(b => b.id === bin_id);
    if (!bin) {
      toast.error(`Bin ${bin_id} not found`);
      return;
    }
    try {
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        toast.error('Popup blocked. Please allow popups and try again.');
        return;
      }
      printWindow.document.write(`
        <html>
          <head>
            <title>Print Bin Label</title>
            <style>
              @media print {
                body { margin: 0; padding: 0; }
                .label {
                  width: 2.5in;
                  height: 1.5in;
                  display: flex;
                  flex-direction: column;
                  align-items: center;
                  justify-content: center;
                  font-family: Arial, sans-serif;
                  padding: 10px;
                  font-weight: bold;
                  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.3);
                }
                .barcode { width: 2in; height: 1in; }
                .bin-name { font-size: 18px; margin-bottom: 5px; }
                .bin-location { font-size: 16px; margin-bottom: 5px; }
                @page { size: 2.5in 1.5in; margin: 0; }
              }
            </style>
          </head>
          <body>
            <div class="label">
              <div class="bin-name">${bin.name || `Bin ${bin.id}`}</div>
              <div class="bin-location">Location: ${bin.location || 'N/A'}</div>
              <canvas id="print-barcode" class="barcode"></canvas>
            </div>
          </body>
        </html>
      `);
      printWindow.document.close();
      JsBarcode(printWindow.document.getElementById('print-barcode'), bin.barcode, {
        format: 'CODE128',
        width: 2,
        height: 50,
        displayValue: true,
        fontSize: 14,
        margin: 0,
      });
      printWindow.focus();
      printWindow.print();
      printWindow.close();
      console.log(`[${new Date().toISOString()}] [BinAssignment] Printed bin ${bin.id}`);
      setSingleBinReady(false);
      setSelectedbin_id('');
    } catch (error) {
      console.error(`Failed to print bin ${bin.id}:`, error);
      toast.error(`Failed to print bin ${bin.id}`);
    }
  };

  // Export bins to CSV
  const exportToCSV = useCallback(() => {
    const headers = ['ID', 'Name', 'Location', 'Barcode'];
    const filteredBins = bins.filter(bin =>
      searchQuery
        ? bin.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          bin.id.toString().includes(searchQuery) ||
          bin.location?.toLowerCase().includes(searchQuery.toLowerCase())
        : true
    );
    const csvContent = [
      headers.join(','),
      ...filteredBins.map(bin =>
        [
          bin.id,
          `"${bin.name || ''}"`,
          `"${bin.location || ''}"`,
          `"${bin.barcode || ''}"`
        ].join(',')
      )
    ].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'bin_locations.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.info('Bin locations exported to CSV');
  }, [bins, searchQuery]);

  // Handle sorting
  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  // Debounce select change
  const handleSelectChange = debounce((value) => {
    console.log('Selected bin ID:', value);
    setSelectedbin_id(value);
    setSingleBinReady(false);
  }, 300);

  // Debounce search input
  const handleSearchChange = debounce((value) => {
    setSearchQuery(value);
  }, 300);

  // Filter and sort bins
  const filteredAndSortedBins = useMemo(() => {
    let filtered = bins.filter(bin =>
      searchQuery
        ? bin.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          bin.id.toString().includes(searchQuery) ||
          bin.location?.toLowerCase().includes(searchQuery.toLowerCase())
        : true
    );
    return filtered.sort((a, b) => {
      const valueA = sortBy === 'id' ? a.id : a.location?.toLowerCase() || '';
      const valueB = sortBy === 'id' ? b.id : b.location?.toLowerCase() || '';
      if (sortBy === 'id') {
        return sortOrder === 'asc' ? valueA - valueB : valueB - valueA;
      }
      return sortOrder === 'asc'
        ? valueA.localeCompare(valueB)
        : valueB.localeCompare(valueA);
    });
  }, [bins, searchQuery, sortBy, sortOrder]);

  return (
    <Container className="p-4" role="main" aria-labelledby="bin-assignment-title">
      <h2 id="bin-assignment-title" className="text-2xl font-bold mb-4">
        Bin Assignment
      </h2>
      <div className="mb-3">
        <Button
          onClick={() => {
            setScanning(!scanning);
            if (!scanning) {
              setScanPhase('sku');
              setSkuScanStatus(null);
              setLastScanned({ type: null, data: null, isDraft: false, productId: null });
            }
          }}
          variant="primary"
          className="mr-2"
          style={{ backgroundColor: '#1E40AF', borderColor: '#1E40AF', fontWeight: 'bold' }}
          aria-label={scanning ? 'Stop barcode scanning' : 'Start barcode scanning'}
          disabled={cameraPermission === 'denied'}
        >
          {scanning ? 'Stop Scanning' : 'Start Scanning'}
        </Button>
        <Button
          onClick={handleResetScan}
          variant="secondary"
          style={{ fontWeight: 'bold' }}
          aria-label="Reset scan state"
        >
          Reset Scan
        </Button>
      </div>
      <Form.Group className="mb-3">
        <Form.Check
          type="checkbox"
          label="Stop scanning after successful bin assignment"
          checked={autoCloseAfterAssignment}
          onChange={(e) => setAutoCloseAfterAssignment(e.target.checked)}
          aria-label="Toggle auto-close after bin assignment"
        />
      </Form.Group>
      {cameraPermission === 'denied' && (
        <p className="text-red-600 mb-2" aria-live="polite">
          Camera access denied. Please enable permissions in your browser settings.
        </p>
      )}
      {scanning && cameraPermission !== 'denied' && (
        <>
          <div className="flex items-center mb-2" aria-live="polite">
            {skuScanStatus === 'success' && scanPhase === 'bin' ? (
              <div className="flex items-center text-green-600">
                <CheckCircleFill className="mr-2" size={20} />
                <span>Scanned SKU: {lastScanned.data}. Scan bin barcode.</span>
              </div>
            ) : skuScanStatus === 'error' ? (
              <span className="text-red-600">Scan failed. Try again.</span>
            ) : (
              <span className="text-blue-600">
                {scanPhase === 'sku' ? 'Scan SKU barcode' : 'Scan bin barcode'}
              </span>
            )}
          </div>
          <div
            id="scanner-container"
            className="w-[300px] h-[300px] border border-gray-300 mb-4"
            aria-label="Barcode scanner"
          />
        </>
      )}
      <InputGroup className="mb-4">
        <FormControl
          type="text"
          value={manualBarcode}
          onChange={(e) => setManualBarcode(e.target.value)}
          placeholder={`Manually enter ${scanPhase === 'sku' ? 'SKU' : 'bin'} barcode...`}
          aria-label={`Manual ${scanPhase === 'sku' ? 'SKU' : 'bin'} barcode input`}
        />
        <Button
          onClick={handleManualSubmit}
          disabled={!manualBarcode || isScanning}
          style={{ backgroundColor: '#1E40AF', borderColor: '#1E40AF', fontWeight: 'bold' }}
          aria-label={`Submit manual ${scanPhase === 'sku' ? 'SKU' : 'bin'} barcode`}
        >
          Submit Barcode
        </Button>
      </InputGroup>
      {isLoadingBins ? (
        <p className="text-gray-500">Loading bins...</p>
      ) : bins.length > 0 ? (
        <div className="mb-6">
          <h3 className="text-xl font-semibold mb-2">Bin Barcodes and Locations</h3>
          <div className="flex flex-col sm:flex-row sm:space-x-4 mb-4">
            <Button
              onClick={printAllBins}
              variant="success"
              className="mb-2 sm:mb-0"
              style={{ backgroundColor: '#047857', borderColor: '#047857', fontWeight: 'bold' }}
              aria-label="Print all bin barcodes"
            >
              Print All Bin Barcodes
            </Button>
            <Button
              onClick={exportToCSV}
              variant="info"
              className="mb-2 sm:mb-0"
              style={{ backgroundColor: '#6B21A8', borderColor: '#6B21A8', fontWeight: 'bold' }}
              aria-label="Export bins to CSV"
            >


              Export to CSV
            </Button>
            <div className="flex items-center">
              <select
                value={selectedbin_id}
                onChange={(e) => handleSelectChange(e.target.value)}
                className="border border-gray-300 rounded px-2 py-1 mr-2 w-full sm:w-auto focus:outline-none focus:ring-2 focus:ring-gray-500"
                aria-label="Select a bin to print"
                disabled={isGeneratingSingleBarcode}
              >
                <option value="">Select a bin</option>
                {bins.map(bin => (
                  <option key={bin.id} value={bin.id}>
                    {bin.name || `ID ${bin.id}`}
                  </option>
                ))}
              </select>
              <Button
                onClick={printSingleBin}
                variant="primary"
                className="ml-2"
                style={{ backgroundColor: '#0D9488', borderColor: '#0D9488', fontWeight: 'bold' }}
                disabled={!selectedbin_id || !singleBinReady || isGeneratingSingleBarcode}
                aria-label="Print selected bin barcode"
              >
                Print Selected Bin
              </Button>
            </div>
          </div>
          <div className="mb-4">
            <input
              type="text"
              placeholder="Search bins by ID, name, or location..."
              onChange={(e) => handleSearchChange(e.target.value)}
              className="border border-gray-300 rounded px-2 py-1 w-full sm:w-1/2 focus:outline-none focus:ring-2 focus:ring-gray-500"
              aria-label="Search bins"
            />
          </div>
          <div className="mb-4">
            <label className="mr-2">Sort by:</label>
            <Button
              onClick={() => handleSort('id')}
              variant={sortBy === 'id' ? 'secondary' : 'light'}
              className="mr-2"
              style={{ fontWeight: 'bold' }}
              aria-label="Sort by ID"
            >
              ID {sortBy === 'id' && (sortOrder === 'asc' ? '↑' : '↓')}
            </Button>
            <Button
              onClick={() => handleSort('location')}
              variant={sortBy === 'location' ? 'secondary' : 'light'}
              style={{ fontWeight: 'bold' }}
              aria-label="Sort by location"
            >
              Location {sortBy === 'location' && (sortOrder === 'asc' ? '↑' : '↓')}
            </Button>
          </div>
          <div ref={componentRef} className="overflow-x-auto">
            <table className="min-w-full border-collapse border border-gray-200">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border border-gray-200 px-4 py-2 text-left">ID</th>
                  <th className="border border-gray-200 px-4 py-2 text-left">Name</th>
                  <th className="border border-gray-200 px-4 py-2 text-left">Location</th>
                  <th className="border border-gray-200 px-4 py-2 text-left">Barcode</th>
                </tr>
              </thead>
              <tbody>
                {filteredAndSortedBins.map(bin => (
                  <tr key={bin.id} className="hover:bg-gray-50">
                    <td className="border border-gray-200 px-4 py-2">{bin.id}</td>
                    <td className="border border-gray-200 px-4 py-2">{bin.name || 'N/A'}</td>
                    <td className="border border-gray-200 px-4 py-2">{bin.location || 'N/A'}</td>
                    <td className="border border-gray-200 px-4 py-2">
                      <canvas
                        id={`barcode-${bin.id}`}
                        className="max-w-full"
                        style={{ maxWidth: '150px' }}
                        aria-label={`Barcode for bin ${bin.name || bin.id}`}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div
            ref={singleBinPrintRef}
            style={{ position: 'absolute', left: '-9999px', width: '2.5in', height: '1.5in', padding: '10px' }}
            aria-hidden="true"
          >
            <div className="bin-name" style={{ fontSize: '16px', marginBottom: '5px' }}></div>
            <div className="bin-location" style={{ fontSize: '14px', marginBottom: '5px' }}></div>
            <canvas id="single-bin-barcode" style={{ width: '2in', height: '1in' }} />
          </div>
        </div>
      ) : (
        <p className="text-gray-500">No bins available</p>
      )}
      {isLoadingDrafts ? (
        <p className="text-gray-500">Loading drafts...</p>
      ) : draftsWithoutBins.length > 0 ? (
        <div>
          <h3 className="text-xl font-semibold mb-2">Drafts Without Bins</h3>
          <ul className="list-disc pl-5">
            {draftsWithoutBins.map(draft => (
              <li key={draft.id} className="mb-1">
                SKU: {draft.sku}, Barcode: {draft.barcode || 'N/A'}
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <p className="text-gray-500">No drafts without bins</p>
      )}
      {isLoadingInventory ? (
        <p className="text-gray-500">Loading inventory products...</p>
      ) : inventoryWithoutBins.length > 0 ? (
        <div>
          <h3 className="text-xl font-semibold mb-2">Inventory Products Without Bins</h3>
          <ul className="list-disc pl-5">
            {inventoryWithoutBins.map(product => (
              <li key={product.id} className="mb-1">
                SKU: {product.sku}, Barcode: {product.barcode || 'N/A'}
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <p className="text-gray-500">No inventory products without bins</p>
      )}
    </Container>
  );
};

export default BinAssignment;